// frontend/src/components/Layout/CustomerAccountBottomNav.tsx
// Mobile bottom navigation for customer account layout

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Calendar, 
  MessageSquare, 
  User, 
  Search 
} from 'lucide-react';

interface BottomNavItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  badge?: string;
}

const bottomNavItems: BottomNavItem[] = [
  {
    name: 'Overview',
    href: '/account',
    icon: Home
  },
  {
    name: 'Bookings',
    href: '/account/bookings',
    icon: Calendar,
    badge: '2'
  },
  {
    name: 'Browse',
    href: '/services',
    icon: Search
  },
  {
    name: 'Messages',
    href: '/account/messages',
    icon: MessageSquare,
    badge: '1'
  },
  {
    name: 'Profile',
    href: '/account/profile',
    icon: User
  }
];

interface CustomerAccountBottomNavProps {
  className?: string;
}

const CustomerAccountBottomNav: React.FC<CustomerAccountBottomNavProps> = ({ 
  className = '' 
}) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Check if current path matches navigation item
  const isActivePath = (href: string) => {
    if (href === '/account' && currentPath === '/account') {
      return true;
    }
    if (href !== '/account' && currentPath.startsWith(href)) {
      return true;
    }
    return false;
  };

  return (
    <>
      {/* Bottom Navigation - Fixed to bottom */}
      <div className={`sm:hidden fixed bottom-0 left-0 right-0 z-50 ${className}`}>
        <div className="bg-white border-t border-gray-200 shadow-lg">
          <div className="grid grid-cols-5 h-16">
            {bottomNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(item.href);
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    flex flex-col items-center justify-center space-y-1 p-2 transition-colors relative
                    ${isActive 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="relative">
                    <Icon 
                      className={`h-5 w-5 ${
                        isActive ? 'text-blue-600' : 'text-gray-500'
                      }`} 
                    />
                    {item.badge && (
                      <span className="absolute -top-2 -right-2 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs font-medium ${
                    isActive ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {item.name}
                  </span>
                  
                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-blue-600 rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Spacer to prevent content from being hidden behind bottom nav */}
      <div className="sm:hidden h-16" />
    </>
  );
};

export default CustomerAccountBottomNav;